// Shared helpers for the report runner.

import type { ReportCell, ReportColumnType, ReportTable } from './useReports';

export function formatCell(value: ReportCell, type: ReportColumnType): string {
  if (value === null || value === undefined) return '';
  if (type === 'currency') {
    const num = typeof value === 'number' ? value : Number(value);
    if (Number.isNaN(num)) return String(value);
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
    }).format(num);
  }
  if (type === 'percent') {
    const num = typeof value === 'number' ? value : Number(value);
    if (Number.isNaN(num)) return String(value);
    return `${num.toFixed(1)}%`;
  }
  if (type === 'number') {
    const num = typeof value === 'number' ? value : Number(value);
    if (Number.isNaN(num)) return String(value);
    return new Intl.NumberFormat('en-GB').format(num);
  }
  if (type === 'date') {
    if (typeof value !== 'string') return String(value);
    const dt = new Date(value);
    if (Number.isNaN(dt.getTime())) return value;
    return dt.toLocaleDateString('en-GB');
  }
  return String(value);
}

function escapeCsvField(value: ReportCell): string {
  if (value === null || value === undefined) return '';
  const str = String(value);
  // RFC 4180: quote if contains commas, quotes, or line breaks.
  if (/[",\r\n]/.test(str)) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

export function buildCsv(table: ReportTable): string {
  const header = table.columns.map((c) => escapeCsvField(c.label)).join(',');
  const lines = [header];
  for (const row of table.rows) {
    const cells = table.columns.map((c) => escapeCsvField(row[c.key] ?? null));
    lines.push(cells.join(','));
  }
  // BOM (U+FEFF) ensures Excel detects UTF-8 when opening the file.
  const bom = String.fromCharCode(0xfeff);
  return `${bom}${lines.join('\r\n')}`;
}

export function downloadCsv(filename: string, content: string): void {
  if (typeof document === 'undefined') return;
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function todayIsoDate(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function startOfMonthIsoDate(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  return `${y}-${m}-01`;
}
